// src/pages/SessionsPage.js
// Shows chats grouped by sessionId
import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthProvider';
import { decodeJwtPayload, getEmailFromClaims } from '../utils/jwt';

const API_BASE = (process.env.REACT_APP_API_BASE || 'http://localhost:5000') + '/analytics';

// Complete phrases that indicate the bot couldn't answer
// The bot will always include the entire phrase if it cannot answer
const UNANSWERED_PHRASES = [
  'My knowledge is constantly expanding. I cannot answer this question yet. May I display the hotline form to help answer your question?',
  'Mein Wissen erweitert sich ständig. Ich kann diese Frage derzeit noch nicht beantworten. Darf ich das Hotline-Formular anzeigen, um Ihnen bei Ihrer Anfrage zu helfen?',
  'Mes connaissances s\'élargissent continuellement. Je ne peux pas encore répondre à cette question. Puis-je afficher le formulaire de hotline pour vous aider ?',
  'Le mie conoscenze sono in continua espansione. Non posso ancora rispondere a questa domanda. Posso mostrare il modulo dell\'hotline per aiutarti?',
  'Мои знания постоянно расширяются. Я пока не могу ответить на этот вопрос. Хотите, я открою форму горячей линии, чтобы помочь вам?',
  '我的知识正在不断扩展，目前还无法回答这个问题。是否需要我为您打开服务热线表单以帮助解答？'
];

const INTERNAL_COMPANIES = [
  "TEDATA GmbH",
  "TEDATa",
  "Driveconcepts GmbH",
  "MDESIGN",
  "MDESIGN Vertriebs GmbH",
];

const INTERNAL_COMPANIES_NORMALIZED = INTERNAL_COMPANIES.map((name) => name.toLowerCase());

// check if a bot output contains any of the complete unanswered phrases
const isUnanswered = (output) => {
  if (!output || typeof output !== 'string') return false;
  return UNANSWERED_PHRASES.some((phrase) => output.includes(phrase));
};

const SessionsPage = () => {
  const { token } = useAuth();
  const claims = useMemo(() => decodeJwtPayload(token), [token]);
  const commentor = useMemo(() => getEmailFromClaims(claims), [claims]);
  const [sessions, setSessions] = useState({});
  const [loading, setLoading] = useState(true);
  const [selectedSession, setSelectedSession] = useState(null);
  //helper funktion //getter
  const getMsgs = (sessions, id) => (Array.isArray(sessions?.[id]) ? sessions[id] : []);
//

useEffect(() => {
  async function fetchSessions() {
    setLoading(true);
    try {
      const res = await axios.get(`${API_BASE}/sessions`);

      const data = res.data && typeof res.data === "object" ? res.data : {};
      setSessions(data);

      const sessionIds = Object.keys(data);

      if (sessionIds.length > 0) {
        const newest = sessionIds
          .map((id) => {
            const msgs = getMsgs(sessions, id);
            const latest = msgs.reduce((m, x) => {
              const ts = x?.timestamp;
              if (!ts) return m;
              if (!m) return ts;
              return new Date(ts) > new Date(m) ? ts : m;
            }, null);
            return { id, latest };
          })
          .sort((a, b) => {
            const ta = a.latest ? new Date(a.latest).getTime() : 0;
            const tb = b.latest ? new Date(b.latest).getTime() : 0;
            return tb - ta;
          });

        setSelectedSession(newest[0].id);
      }
    } catch (err) {
      console.error("Error fetching sessions:", err);
    }
    setLoading(false);
  }
  fetchSessions();
}, []);


  // initialize default period to Day on first load
  useEffect(() => {
    applyPeriod('day');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const sessionIds = Object.keys(sessions);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [period, setPeriod] = useState('day');
  const [externOnly, setExternOnly] = useState(false);
  const [agency, setAgency] = useState('all'); // 'all' | specific commentor
  const [gruppeFilter, setGruppeFilter] = useState('all'); // 'all' | 'leer' | 'content' | 'platform'
  const [mode, setMode] = useState('all'); // 'all' | 'unanswered' | 'red' | 'yellow'
  const [ratingMenuOpen, setRatingMenuOpen] = useState(null); // msgId or null
  const [ratingComment, setRatingComment] = useState('');
  const messages = selectedSession ? sessions[selectedSession] : [];

  const agencyOptions = useMemo(() => {
    const values = new Set();
    Object.values(sessions).forEach((msgs) => {
      if (!Array.isArray(msgs)) return;
      msgs.forEach((m) => {
        const commentorName = (m?.commentor || '').trim();
        const agenturName = (m?.Agentur || '').trim();
        if (commentorName) values.add(commentorName);
        if (agenturName) values.add(agenturName);
      });
    });
    return Array.from(values).sort((a, b) => a.localeCompare(b));
  }, [sessions]);

  // Helper to apply quick period selection (day/week/month/year)
  const applyPeriod = (value) => {
    setPeriod(value);
    if (value === 'custom') return;
    const today = new Date();
    const end = new Date(today);
    const start = new Date(today);
    if (value === 'day') start.setDate(start.getDate() - 1);
    if (value === 'week') start.setDate(start.getDate() - 7);
    if (value === 'month') start.setMonth(start.getMonth() - 1);
    if (value === 'year') start.setFullYear(start.getFullYear() - 1);
    const toDateStr = (d) => d.toISOString().slice(0, 10);
    setEndDate(toDateStr(end));
    setStartDate(toDateStr(start));
  };

  // Helper to save message rating (status and/or comment) to backend
  const saveMessageRating = async (msgId, sessionId, status, comment, gruppe, Agentur) => {
    try {
      const payload = { msgId, sessionId, status, comment, commentor, gruppe, Agentur };
      await axios.patch(`${API_BASE}/messages`, payload);
      // Refresh sessions to reflect updated data
      const res = await axios.get(`${API_BASE}/sessions`);
      setSessions(res.data);
      setRatingMenuOpen(null);
      setRatingComment('');
    } catch (err) {
      console.error('Error saving rating:', err);
    }
  };

  const getGruppeButtonClass = (currentGruppe, value) => {
    const baseClass = 'flex-1 px-2 py-1 rounded text-sm transition-colors';
    if ((currentGruppe || '').toLowerCase() === value) {
      if (value === 'content') return `${baseClass} bg-blue-500 text-white hover:bg-blue-600`;
      if (value === 'platform') return `${baseClass} bg-blue-500 text-white hover:bg-blue-600`;
      return `${baseClass} bg-blue-500 text-white hover:bg-blue-600`;
    }
    return `${baseClass} bg-blue-100 text-blue-800 hover:bg-blue-200`;
  };

  // Helper to get status color for display
  const getStatusColor = (status) => {
    if (status === 'red') return 'bg-red-100 border-l-4 border-red-500';
    if (status === 'yellow') return 'bg-yellow-100 border-l-4 border-yellow-500';
    if (status === 'green') return 'bg-green-100 border-l-4 border-green-500';
    return '';
  };

  // Helper: return the latest timestamp string for a session (or null)
  const getSessionLatestTimestamp = (id) => {
    const msgs = getMsgs(sessions, id);
    if (msgs.length === 0) return null;
    let latest = null;
    for (const m of msgs) {
      if (!m.timestamp) continue;
      if (!latest || new Date(m.timestamp) > new Date(latest)) latest = m.timestamp;
    }
    return latest;
  };

  // Helper to get display info for a session, including unanswered count
  const getSessionInfo = (id) => {
    const msgs = getMsgs(sessions, id);
    // Use first message for firma and timestamp
    if (msgs.length === 0) return { firma: '', timestamp: '', sessionId: id, unansweredCount: 0, statusList: [] };
    const { firma = '', timestamp = '', user = '' } = msgs[0];

    // count unanswered messages in this session
    const unansweredCount = msgs.reduce((acc, m) => acc + (isUnanswered(m.output) ? 1 : 0), 0);
    
    // get all unique statuses in this session
    const statusList = [...new Set(msgs.map(m => m.status).filter(s => s))];

    return { firma, timestamp, user, sessionId: id, unansweredCount, statusList };
  };



  return (
    <div className="max-w-7xl mx-auto bg-white rounded-lg shadow p-0 flex h-[80vh]">
      {/* Sidebar: Session list */}
      <div className="w-1/3 border-r p-6 overflow-y-auto">
        <h2 className="text-lg font-bold mb-4">Unterhaltungen</h2>
        {/* Date range filter UI (sticky inside the sidebar so it stays visible while scrolling) */}
        <div className="mb-4 sticky top-0 bg-white z-10 py-2 flex flex-col gap-2">
          <div className="flex items-center justify-between">
            <label className="text-xs text-gray-500" htmlFor="extern-only-toggle">Nur Extern</label>
            <button
              id="extern-only-toggle"
              type="button"
              role="switch"
              aria-checked={externOnly}
              onClick={() => setExternOnly((v) => !v)}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${externOnly ? 'bg-blue-600' : 'bg-gray-300'}`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${externOnly ? 'translate-x-6' : 'translate-x-1'}`}
              />
            </button>
          </div>
          <div className="flex flex-col">
            <label className="text-xs text-gray-500">Agentur</label>
            <select
              className="mt-1 p-2 border rounded"
              value={agency}
              onChange={(e) => setAgency(e.target.value)}
            >
              <option value="all">Alle Agenturen</option>
              {agencyOptions.map((name) => (
                <option key={name} value={name}>{name}</option>
              ))}
            </select>
          </div>
          <div className="flex flex-col">
            <label className="text-xs text-gray-500">Gruppe</label>
            <select
              className="mt-1 p-2 border rounded"
              value={gruppeFilter}
              onChange={(e) => setGruppeFilter(e.target.value)}
            >
              <option value="all">Alle Gruppen</option>
              <option value="leer">Leer</option>
              <option value="content">Content</option>
              <option value="platform">Platform</option>
            </select>
          </div>
          {/* Put Modus under Gruppe */}
          <div className="flex flex-col">
            <label className="text-xs text-gray-500">Modus</label>
            <select
              className="mt-1 p-2 border rounded"
              value={mode}
              onChange={(e) => setMode(e.target.value)}
            >
              <option value="all">Alle Unterhaltungen</option>
              <option value="unanswered">Unbeantwortete Fragen</option>
              <option value="red">Rot</option>
              <option value="yellow">Gelb</option>
            </select>
          </div>
          {/* Zeitraum under Modus */}
          <div className="flex flex-col">
            <label className="text-xs text-gray-500">Zeitraum</label>
            <select
              className="mt-1 p-2 border rounded"
              value={period}
              onChange={(e) => applyPeriod(e.target.value)}
            >
              <option value="custom">Benutzerdefiniert</option>
              <option value="day">Tag</option>
              <option value="week">Woche</option>
              <option value="month">Monat</option>
              <option value="year">Jahr</option>
            </select>
          </div>
          <div className="flex items-end gap-2">
            <div className="flex flex-col">
              <label className="text-xs text-gray-500">Von</label>
              <input
                type="date"
                className="mt-1 p-2 border rounded"
                value={startDate}
                onChange={(e) => { setStartDate(e.target.value); setPeriod('custom'); }}
              />
            </div>
            <div className="flex flex-col">
              <label className="text-xs text-gray-500">Bis</label>
              <input
                type="date"
                className="mt-1 p-2 border rounded"
                value={endDate}
                onChange={(e) => { setEndDate(e.target.value); setPeriod('custom'); }}
              />
            </div>
            <button
              className="ml-2 px-3 py-2 bg-gray-100 rounded text-sm"
              onClick={() => { setStartDate(''); setEndDate(''); setPeriod('custom'); }}
            >
              Löschen
            </button>
          </div>
        </div>
        {loading ? (
          <div className="text-gray-500">Wird geladen...</div>
        ) : sessionIds.length === 0 ? (
          <div className="text-gray-500">Keine Sitzungen gefunden.</div>
        ) : (
          <ul className="space-y-2">
              {sessionIds
                .filter((id) => {
                  if (!startDate && !endDate) return true;

                  const msgs = getMsgs(sessions, id);
                  if (msgs.length === 0) return false;

                  return msgs.some((msg) => {
                    if (!msg.timestamp) return false;
                    const msgDate = new Date(msg.timestamp);

                    if (startDate) {
                      const start = new Date(startDate + 'T00:00:00');
                      if (msgDate < start) return false;
                    }
                    if (endDate) {
                      const end = new Date(endDate + 'T23:59:59.999');
                      if (msgDate > end) return false;
                    }
                    return true;
                  });
                })
                .filter((id) => {
                  if (!externOnly) return true;

                  const msgs = getMsgs(sessions, id);
                  return msgs.some((m) => {
                    const firma = (m.firma || '').trim().toLowerCase();
                    return !INTERNAL_COMPANIES_NORMALIZED.includes(firma);
                  });
                })
                .filter((id) => {
                  if (agency === 'all') return true;

                  const msgs = getMsgs(sessions, id);
                  const selectedAgency = agency.toLowerCase();
                  return msgs.some((m) => {
                    const commentorName = (m?.commentor || '').trim().toLowerCase();
                    const agenturName = (m?.Agentur || '').trim().toLowerCase();
                    return commentorName === selectedAgency || agenturName === selectedAgency;
                  });
                })
                .filter((id) => {
                  if (gruppeFilter === 'all') return true;

                  const msgs = getMsgs(sessions, id);
                  const selectedGruppe = gruppeFilter.toLowerCase();
                  if (selectedGruppe === 'leer') {
                    return msgs.some((m) => {
                      const gruppe = (m?.gruppe || '').trim().toLowerCase();
                      return gruppe !== 'content' && gruppe !== 'platform';
                    });
                  }
                  return msgs.some((m) => ((m?.gruppe || '').trim().toLowerCase() === selectedGruppe));
                })
                .filter((id) => {
                  if (mode === 'all') return true;

                  const msgs = getMsgs(sessions, id);
                  if (mode === 'unanswered') return msgs.some((m) => isUnanswered(m.output));
                  if (mode === 'red') return msgs.some((m) => m.status === 'red');
                  if (mode === 'yellow') return msgs.some((m) => m.status === 'yellow');
                  return true;
                })
              // sort by latest message timestamp descending (newest first)
              .sort((a, b) => {
                const ta = getSessionLatestTimestamp(a) ? new Date(getSessionLatestTimestamp(a)).getTime() : 0;
                const tb = getSessionLatestTimestamp(b) ? new Date(getSessionLatestTimestamp(b)).getTime() : 0;
                return tb - ta;
              })
              .map((id) => {
                const info = getSessionInfo(id);
                const { firma, timestamp, sessionId, user, unansweredCount, statusList } = info;
                return (
                  <li key={id}>
                    <button
                      className={`w-full text-left px-3 py-2 rounded-lg transition-colors ${selectedSession === id ? 'bg-blue-100 text-blue-900 font-bold' : 'hover:bg-gray-100 text-gray-700'}`}
                      onClick={() => setSelectedSession(id)}
                    >
                      <div className="flex items-start justify-between">
                        <div className="min-w-0">
                          <div className="text-base font-semibold truncate">{firma || 'Unbekannte Firma'}</div>
                          <div className="text-sm text-gray-500">{user || 'Unbekannter Benutzer'}</div>
                          <div className="text-xs text-gray-400 font-mono truncate">{sessionId}</div>
                        </div>
                        <div className="ml-3 flex-shrink-0 flex flex-col gap-1">
                          {statusList.length > 0 && (
                            <div className="flex gap-1">
                              {statusList.map((status) => (
                                <span
                                  key={status}
                                  className={`inline-block w-3 h-3 rounded-full ${
                                    status === 'red' ? 'bg-red-500' : status === 'yellow' ? 'bg-yellow-500' : 'bg-green-500'
                                  }`}
                                />
                              ))}
                            </div>
                          )}
                          {unansweredCount > 0 && (
                            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                              {unansweredCount} unbeantwortet
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="text-xs text-gray-500 mt-1">{timestamp ? new Date(timestamp).toLocaleString() : ''}</div>
                    </button>
                  </li>
                );
              })}
          </ul>
        )}
      </div>
      {/* Main: Chat view */}
      <div className="flex-1 p-6 overflow-y-auto">
        <h2 className="text-lg font-bold mb-4">Chat</h2>
        {selectedSession && messages.length > 0 ? (
          <div className="flex flex-col gap-2">
            {messages.map((msg, idx) => {
              const unanswered = isUnanswered(msg.output);
              return (
                <React.Fragment key={idx}>
                  {/* User message bubble */}
                  <div className="flex justify-start mb-1">
                    <div className={`rounded-lg px-4 py-2 max-w-xl ${unanswered ? 'bg-yellow-50 border-l-4 border-red-500' : 'bg-blue-100 text-blue-900'}`}>
                      <div className="flex items-center gap-2">
                        <div className="font-semibold">Benutzer:</div>
                        {unanswered && <div className="text-xs text-red-700 font-semibold">Unbeantwortet</div>}
                      </div>
                      <div className="whitespace-pre-wrap mt-1 text-gray-800">{msg.input}</div>
                      <div className="text-xs text-gray-400 mt-1">{msg.timestamp}</div>
                    </div>
                  </div>
                  {/* Bot message bubble (right-aligned, label above message) */}
                  <div className="flex justify-end mb-3">
                    <div className={`text-gray-900 rounded-lg px-4 py-2 max-w-xl relative ${msg.status ? getStatusColor(msg.status) : 'bg-gray-100'}`}>
                      <div className="flex justify-between items-center">
                        <div className="font-semibold">Bot:</div>
                        {msg.gruppe && <div className="text-xs text-gray-500 flex-1 text-center">Gruppe: {msg.gruppe}</div>}
                        <button
                          className="text-gray-400 hover:text-gray-600 text-xl"
                          onClick={() => {
                            const nextOpen = ratingMenuOpen === msg.id ? null : msg.id;
                            setRatingMenuOpen(nextOpen);
                            if (nextOpen) setRatingComment(msg.comment || '');
                          }}
                        >
                          ⋯
                        </button>
                      </div>
                      <div className="whitespace-pre-wrap mt-1 text-gray-800">{msg.output}</div>
                      {msg.comment && <div className="text-xs text-gray-600 mt-1 italic">💬 {msg.comment}</div>}
                      {msg.commentor && (
                        <div className="text-xs text-gray-500 mt-1">Bearbeitet von: {msg.commentor}</div>
                      )}
                      {msg.Agentur && (
                        <div className="text-xs text-gray-500 mt-1">Agentur: {msg.Agentur}</div>
                      )}
                      <div className="text-xs text-gray-400 mt-1 text-right">{msg.timestamp}</div>

                      {/* Rating dropdown menu */}
                      {ratingMenuOpen === msg.id && (
                        <div className="absolute right-0 top-10 bg-white border rounded-lg shadow-lg p-3 z-50 w-56">
                          <div className="text-sm font-semibold mb-2">Bewertung:</div>
                          <div className="flex gap-2 mb-3">
                            <button
                              className="flex-1 px-2 py-1 bg-red-100 text-red-800 rounded text-sm hover:bg-red-200"
                              onClick={() => saveMessageRating(msg.id, msg.sessionId, 'red', ratingComment)}
                            >
                              🔴 Prio 1
                            </button>
                            <button
                              className="flex-1 px-2 py-1 bg-yellow-100 text-yellow-800 rounded text-sm hover:bg-yellow-200"
                              onClick={() => saveMessageRating(msg.id, msg.sessionId, 'yellow', ratingComment)}
                            >
                              🟡 Prio 2
                            </button>
                            <button
                              className="flex-1 px-2 py-1 bg-green-100 text-green-800 rounded text-sm hover:bg-green-200"
                              onClick={() => saveMessageRating(msg.id, msg.sessionId, 'green', ratingComment)}
                            >
                              🟢 Erledigt
                            </button>
                          </div>
                          <div className="text-sm font-semibold mb-2">Gruppe:</div>
                          <div className="flex gap-2 mb-3">
                            <button
                              className={getGruppeButtonClass(msg.gruppe, 'leer')}
                              onClick={() => saveMessageRating(msg.id, msg.sessionId, msg.status || undefined, ratingComment, 'leer')}
                            >
                              Leer
                            </button>
                            <button
                              className={getGruppeButtonClass(msg.gruppe, 'content')}
                              onClick={() => saveMessageRating(msg.id, msg.sessionId, msg.status || undefined, ratingComment, 'content')}
                            >
                              Content
                            </button>
                            <button
                              className={getGruppeButtonClass(msg.gruppe, 'platform')}
                              onClick={() => saveMessageRating(msg.id, msg.sessionId, msg.status || undefined, ratingComment, 'platform')}
                            >
                              Platform
                            </button>
                          </div>
                          <div className="text-sm font-semibold mb-1">Agentur:</div>
                          <select
                            className="w-full text-xs border rounded p-1 mb-2"
                            value={msg.Agentur || ''}
                            onChange={(e) => saveMessageRating(msg.id, msg.sessionId, msg.status || undefined, ratingComment, msg.gruppe || undefined, e.target.value || undefined)}
                          >
                            <option value="">Agentur waehlen...</option>
                            {agencyOptions.map((name) => (
                              <option key={name} value={name}>{name}</option>
                            ))}
                          </select>
                          <div className="text-sm font-semibold mb-1">Kommentar:</div>
                          <textarea
                            className="w-full text-xs border rounded p-1 mb-2"
                            rows="2"
                            placeholder="Kommentar hinzufügen..."
                            value={ratingComment}
                            onChange={(e) => setRatingComment(e.target.value)}
                          />
                          <button
                            className="w-full px-2 py-1 bg-blue-500 text-white rounded text-sm hover:bg-blue-600"
                            onClick={() => saveMessageRating(msg.id, msg.sessionId, msg.status || undefined, ratingComment)}
                          >
                            Speichern
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                </React.Fragment>
              );
            })}
          </div>
        ) : (
          <div className="text-gray-500">Keine Nachrichten für diese Sitzung.</div>
        )}
      </div>
    </div>
  );
};

export default SessionsPage;
